'use client';

import { useMemo } from 'react';
import {
	BigNum,
	QUOTE_PRECISION_EXP,
	SpotMarketConfig,
	ZERO,
	calculateMaxRemainingDeposit,
	isUserBankrupt,
} from '@velocity-exchange/sdk';

import useVelocityClient from 'src/hooks/useVelocityClient';
import useVelocityClientIsReady from 'src/hooks/useVelocityClientIsReady';
import useAccountTargetSpotBalance from 'src/hooks/useAccountTargetSpotBalance';
import { AccountData } from 'src/stores/useVelocityAccountsStore';
import { isAmplifyAccountName } from 'src/utils/amplify';
import { getCloseBorrowAmountWithBuffer } from 'src/utils/borrowLend';

/**
 * Everything that limits a deposit because of where it is *going*, as opposed
 * to where the funds come from.
 *
 * Every deposit entry point has to clear the same destination rules —
 * remaining market capacity, a bankrupt account, an empty Amplify account —
 * regardless of what the user is paying with. This hook is the single seam
 * for them, so an entry point that funds a deposit differently (a swap, a
 * bridge) can't quietly ship a weaker set of rules than DepositModal's.
 *
 * Source-side limits (wallet balance, SOL for rent, quote freshness) belong to
 * the caller: only the caller knows what it is spending.
 */
export interface DepositDestinationLimits {
	/**
	 * Remaining deposit capacity of the market, or `null` when the market sets
	 * no cap (`maxTokenDeposits === 0`) and capacity is therefore unlimited.
	 */
	marketRemainingDeposits: BigNum | null;
	/** Same as `marketRemainingDeposits`; the only ceiling in play. */
	maxDepositAmount: BigNum | null;
	isAccountBankrupt: boolean;
	/** Amplify accounts can't be topped up while they hold no balance. */
	isEmptyAmplifyAccount: boolean;
}

export const useDepositDestinationLimits = ({
	market,
	targetAccountKey,
	targetAccount,
}: {
	market: SpotMarketConfig;
	/** '' / undefined when the deposit creates a brand-new account. */
	targetAccountKey: string | undefined;
	targetAccount: AccountData | undefined;
}): DepositDestinationLimits => {
	const velocityClient = useVelocityClient();
	const velocityClientIsReady = useVelocityClientIsReady();
	const precisionExp = market?.precisionExp ?? QUOTE_PRECISION_EXP;

	const spotMarketAccount =
		velocityClientIsReady && market && velocityClient
			? velocityClient.getSpotMarketAccount(market.marketIndex)
			: undefined;

	const targetAccountBankBalance = useAccountTargetSpotBalance(
		market,
		targetAccountKey
	);

	// A borrow position must always be closable, even when the market is
	// otherwise at capacity.
	const bufferedCloseBorrowAmount = useMemo(() => {
		if (!velocityClient || !market || !targetAccountBankBalance) {
			return BigNum.zero(precisionExp);
		}

		if (!targetAccountBankBalance.netBaseBalance.isNeg()) {
			return BigNum.zero(precisionExp);
		}

		return getCloseBorrowAmountWithBuffer(
			velocityClient,
			market.marketIndex,
			targetAccountBankBalance.netBaseBalance
		);
	}, [velocityClient, market, targetAccountBankBalance, precisionExp]);

	const marketRemainingDeposits = useMemo(() => {
		if (!spotMarketAccount) return BigNum.zero(precisionExp);
		if ((spotMarketAccount.maxTokenDeposits || ZERO).eq(ZERO)) return null;

		const maxRemainingDeposit = BigNum.from(
			calculateMaxRemainingDeposit(spotMarketAccount),
			precisionExp
		);

		return bufferedCloseBorrowAmount.gtZero()
			? BigNum.max(maxRemainingDeposit, bufferedCloseBorrowAmount)
			: maxRemainingDeposit;
	}, [spotMarketAccount, bufferedCloseBorrowAmount, precisionExp]);

	const maxDepositAmount = marketRemainingDeposits;

	return {
		marketRemainingDeposits,
		maxDepositAmount,
		isAccountBankrupt:
			!!targetAccount?.client && isUserBankrupt(targetAccount.client),
		isEmptyAmplifyAccount:
			!!targetAccount &&
			isAmplifyAccountName(targetAccount.name) &&
			!targetAccount.spotBalances.some((balance) => balance.balance.gtZero()),
	};
};
